import React from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext';
import { AuthScreen } from '../components/AuthScreen';
import { CustomerView } from '../components/CustomerView';
import { CustomerJobHistory } from '../components/CustomerJobHistory';
import { SettingsScreen } from '../components/SettingsScreen';
import { WorkerProfileDetail } from '../components/WorkerProfileDetail';
import { LiveTrackingScreen } from '../components/LiveTrackingScreen';
import { ChatScreen } from '../components/ChatScreen';
import { UserRole } from '../types';
import { updateJobStatus, postJob } from '../services/firestoreService';
import { BookingModal } from '../components/BookingModal';
import { PostJobModal } from '../components/PostJobModal';
import { ActionFunctionArgs } from 'react-router-dom';
import { UserLayout } from './UserLayout';
import { CMSPage } from '../components/CMSPage';
import { TrackingWrapper } from '../components/TrackingWrapper';
import { OnboardingScreen } from '../components/OnboardingScreen';

export const UserRoutes: React.FC = () => {
    const {
        isLoggedIn, role, userProfile, serviceCategories,
        myJobs, handleAuthSuccess, handleOnboardingComplete, performLogout, handleProfileUpdate,
        isOnboarding, setIsOnboarding, chattingJob, setChattingJob
    } = useSession();

    const navigate = useNavigate();

    // Local State (kept for overlays/modals that stay contextually relevant)
    const [selectedWorker, setSelectedWorker] = React.useState<any>(null);
    const [isBooking, setIsBooking] = React.useState(false);
    const [isPostingJob, setIsPostingJob] = React.useState(false);
    const [trackingJob, setTrackingJob] = React.useState<any>(null);

    // If not logged in, show Auth
    if (!isLoggedIn) {
        return (
            <AuthScreen
                onSuccess={async (id, r, name) => {
                    const success = await handleAuthSuccess(id, UserRole.CUSTOMER, name);
                    if (!success) {
                        throw new Error("Failed to load profile. Please try again.");
                    }
                }}
                roleText={UserRole.CUSTOMER}
                onBack={() => window.location.href = '/'}
            />
        );
    }

    // Show onboarding if needed
    if (isOnboarding) {
        return (
            <OnboardingScreen
                role={role}
                initialPhone={userProfile.phone || ''}
                onComplete={async (profile, workerDetails) => {
                    await handleOnboardingComplete(profile, workerDetails);
                    setIsOnboarding(false);
                    navigate('/user/home');
                }}
            />
        );
    }

    if (role !== UserRole.CUSTOMER) {
        return <Navigate to="/" />;
    }

    // Overlays (Chat, Tracking, Worker Detail, Modals)
    // These override the router view when active to preserve state
    if (chattingJob) {
        return <ChatScreen jobId={chattingJob.id} otherName={chattingJob.workerName || 'Expert'} onBack={() => setChattingJob(null)} perspective="customer" />;
    }

    if (trackingJob) {
        return (
            <LiveTrackingScreen
                job={trackingJob}
                perspective="customer"
                onBack={() => setTrackingJob(null)}
                onChat={() => setChattingJob(trackingJob)}
                onCall={() => {
                    if (!userProfile.phone) {
                        alert("Please add your phone number in Profile Settings to make calls.");
                        return;
                    }
                    const targetPhone = trackingJob.workerPhone;
                    // Check if it looks like a phone number (not a UUID)
                    if (targetPhone && /^\+?[\d\s-]{10,}$/.test(targetPhone)) {
                        window.location.href = `tel:${targetPhone.replace(/[^0-9+]/g, '')}`;
                    } else {
                        alert("Expert phone number not available.");
                    }
                }}
            />
        );
    }

    // ✅ FIX: Only show WorkerProfileDetail if NOT booking
    if (selectedWorker && !isBooking) {
        return <WorkerProfileDetail worker={selectedWorker} onBack={() => setSelectedWorker(null)} onBook={() => setIsBooking(true)} />;
    }

    return (
        <div className="h-full w-full">
            <Routes>
                <Route element={<UserLayout />}>
                    <Route index element={<Navigate to="home" replace />} />

                    <Route path="home" element={
                        <CustomerView
                            categories={serviceCategories}
                            onSelectWorker={setSelectedWorker}
                            onBookWorker={(worker) => {
                                console.log("📝 onBookWorker called:", worker.name, "ID:", worker.id);
                                setSelectedWorker(worker);
                                console.log("✅ setSelectedWorker done");
                                setIsBooking(true);
                                console.log("✅ setIsBooking(true) - Modal should open now");
                            }}
                            onOpenPostJob={() => setIsPostingJob(true)}
                            activeJobs={myJobs}
                            onTrackWorker={setTrackingJob}
                        />
                    } />

                    <Route path="activity" element={
                        <CustomerJobHistory
                            jobs={myJobs}
                            onCancelJob={(jobId) => updateJobStatus(jobId, 'CANCELLED', { cancelledBy: 'customer' })}
                            onRateJob={(jobId, rating, text, mediaUrls, voiceNoteUrl) => updateJobStatus(jobId, 'COMPLETED', { userReview: { rating, text, mediaUrls, voiceNoteUrl } })}
                            onTrackJob={setTrackingJob}
                            onChat={setChattingJob}
                            onJobUpdate={(jobId, status, extra) => updateJobStatus(jobId, status, extra)}
                        />
                    } />

                    <Route path="profile" element={
                        <SettingsScreen
                            role={role}
                            onLogout={() => performLogout(true)}
                            userProfile={userProfile}
                            onUpdateProfile={handleProfileUpdate}
                            onNavigateToWorks={() => navigate('/user/activity')}
                        />
                    } />

                    <Route path="privacy-policy" element={<CMSPage slug="privacy" title="Privacy Policy" />} />
                    <Route path="terms-of-services" element={<CMSPage slug="terms" title="Terms of Service" />} />
                    <Route path="help" element={<CMSPage slug="help" title="Help & Support" />} />

                    {/* Tracking Route (Deep Link Support) */}
                    {/* Tracking Route (Deep Link Support) */}
                    <Route path="track" element={<TrackingWrapper />} />
                </Route>
            </Routes>

            {/* Global Modals */}
            {isPostingJob && (
                <PostJobModal
                    categories={serviceCategories}
                    onClose={() => setIsPostingJob(false)}
                    onSubmit={async (data) => {
                        await postJob({
                            ...data,
                            customerId: userProfile.id!,
                            customerName: `${userProfile.firstName} ${userProfile.lastName}`,
                            customerAvatar: userProfile.avatarUrl,
                            customerPhone: userProfile.phone
                        });
                        setIsPostingJob(false);
                        alert("Job Posted!");
                        navigate('/user/activity');
                    }}
                />
            )}

            {(() => {
                console.log("🔍 Modal Check: isBooking=", isBooking, "selectedWorker=", selectedWorker?.name || "null");
                return isBooking && selectedWorker;
                return isBooking && selectedWorker;
            })() && (
                    <BookingModal
                        worker={selectedWorker}
                        onClose={(createdJob) => {
                            setIsBooking(false);
                            if (createdJob) {
                                setSelectedWorker(null);
                                navigate('/user/activity');
                            }
                        }}
                        onConfirm={async (job: any) => {
                            const id = await postJob({
                                ...job,
                                customerId: userProfile.id!,
                                customerName: `${userProfile.firstName} ${userProfile.lastName}`,
                                customerAvatar: userProfile.avatarUrl,
                                customerPhone: userProfile.phone,
                                workerPhone: selectedWorker.phone,
                                workerId: selectedWorker.id
                            });
                            setIsBooking(false);
                            setSelectedWorker(null);
                            // alert("Request Sent!"); // Removed to prevent blocking
                            navigate('/user/activity');
                            return id;
                        }}
                    />
                )}
        </div>
    );
};
